import React from 'react';
import { Star } from 'lucide-react';
import { Contact } from '../types/Contact';
import CustomSelect from './CustomSelect';

interface StatusTierManagerProps {
    formData: Partial<Contact>;
    isEditing?: boolean;
    onInputChange: (field: keyof Contact, value: any) => void;
}

export default function StatusTierManager({ formData, isEditing = false, onInputChange }: StatusTierManagerProps) {
    // Helper function to get tier color
    const getTierColor = (tier: string) => {
        switch (tier) {
            case 'A': return 'text-green-400 bg-green-400/10 border-green-400/20';
            case 'B': return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
            case 'C': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
            case 'D': return 'text-red-400 bg-red-400/10 border-red-400/20';
            default: return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
        }
    };

    // Helper function to get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Called': return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
            case 'Follow-up': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
            case 'Meeting Scheduled': return 'text-green-400 bg-green-400/10 border-green-400/20';
            case 'Closed': return 'text-purple-400 bg-purple-400/10 border-purple-400/20';
            default: return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
        }
    };

    return (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
            <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                <Star className="w-5 h-5 mr-3" />
                STATUS & TIER
            </h3>

            {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-300 mb-2">Tier</label>
                        <CustomSelect
                            options={[
                                { value: 'A', label: 'Tier A' },
                                { value: 'B', label: 'Tier B' },
                                { value: 'C', label: 'Tier C' },
                                { value: 'D', label: 'Tier D' },
                                { value: 'Unassigned', label: 'Unassigned' }
                            ]}
                            value={formData.tier || 'Unassigned'}
                            onChange={(value) => onInputChange('tier', value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-300 mb-2">Status</label>
                        <CustomSelect
                            options={[
                                { value: 'Not Contacted', label: 'Not Contacted' },
                                { value: 'Called', label: 'Called' },
                                { value: 'Follow-up', label: 'Follow-up' },
                                { value: 'Meeting Scheduled', label: 'Meeting Scheduled' },
                                { value: 'Closed', label: 'Closed' }
                            ]}
                            value={formData.status || 'Not Contacted'}
                            onChange={(value) => onInputChange('status', value)}
                        />
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">Tier:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTierColor(formData.tier || 'Unassigned')}`}>
                            {formData.tier || 'Unassigned'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(formData.status || 'Not Contacted')}`}>
                            {formData.status || 'Not Contacted'}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
